import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { env } from './env';

/**
 * Call once at app boot, before the router is created. No-op without a DSN or when VITE_SENTRY_ENABLED=false.
 * Pass extra integrations (e.g. `Sentry.replayIntegration()`) from the app entry so they only ship in that bundle.
 */
export function initSentry(dsn: string | undefined, extraIntegrations: Extract<Sentry.BrowserOptions[`integrations`], unknown[]> = []) {
  if (!dsn || !env.sentryEnabled) return;

  Sentry.init({
    dsn,
    // st.paidnice.com forwards envelopes to Sentry (sentryTunnel function) so
    // reporting survives adblock filter lists that block *.ingest.sentry.io.
    tunnel: `https://st.paidnice.com/tunnel`,
    environment: env.environment,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      ...extraIntegrations,
    ],
    tracesSampleRate: env.isProd ? 0.1 : 1.0,
    // Attach sentry-trace/baggage headers to API calls so one trace spans
    // click -> web request -> pubsub job.
    tracePropagationTargets: [env.apiUrl],
    // Only used when replayIntegration is passed: never record routine sessions,
    // but keep a rolling buffer and upload it when an error is captured.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    // Firebase Analytics' installations SDK opens IndexedDB without a catch;
    // iOS WKWebView purges the DB mid-session and the rejection is unhandled.
    // Analytics-only, no user impact. See PAIDNICE-FRONTEND-A.
    // "Not implemented on this platform" is a bare string rejection from outside
    // our bundle (likely a Safari extension) in an idle background tab. See PAIDNICE-FRONTEND-11.
    ignoreErrors: [/Database deleted by request of the user/, /Not implemented on this platform/],
  });
}

/** React 19 root options: report errors caught by any error boundary (incl. react-router errorElement) and uncaught ones. */
export const sentryRootOptions = {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
};
